// Shared types for the Use-Case Brochure builder — an editorial, photo-led
// problem→solution one-pager (modeled on the "Hospitality Emergency
// Communications" collateral) rendered as a complete-HTML email.
//
// It reuses the brochure-builder's helpers (palette, bulletproof button, doc
// scaffold, url helpers) so the two brochure templates share one design system.

export type UseCaseSectionType = 'hero' | 'challenge' | 'comparison' | 'steps' | 'cta'

export interface UseCaseSection {
  id: string
  type: UseCaseSectionType
  content: any
  backgroundColor?: string
}

// Re-export the shared brochure primitives so the section files import from one
// place (keeps the two brochure builders on the same palette + helpers).
import { BROCHURE_COLORS } from "@crm-src/components/brochure-builder/stock-images"
import { BROCHURE_FONT, escapeHtml } from "@crm-src/components/brochure-builder/html-helpers"
export { BROCHURE_COLORS, XOP_BRAND_MARK_URL, showcaseImageOptions, toAbsoluteUrl, toPreviewImageUrl } from "@crm-src/components/brochure-builder/stock-images"
export type { TenantInfo } from "@crm-src/components/brochure-builder/stock-images"
export { BROCHURE_FONT, ctaButton, escapeHtml } from "@crm-src/components/brochure-builder/html-helpers"

// Split a multiline textarea value into trimmed, non-empty lines. The section
// editors store bullet lists as one-per-line text (far simpler than an
// array-of-objects editor); the *ToHtml serializers split them here.
export function splitLines(value: unknown): string[] {
  return String(value ?? "")
    .split("\n")
    .map((l) => l.trim())
    .filter((l) => l.length > 0)
}

// Serialize a list of strings as clean, email-safe dash bullets — a two-column
// table (marker | text) so long lines wrap with a hanging indent. Uses a
// restrained "–" dash, never a filled "blob" marker.
export function bulletRows(lines: string[], opts: { markerColor?: string; textColor?: string; size?: number } = {}): string {
  // A restrained dash marker (never a filled "blob"). Neutral by default.
  const marker = opts.markerColor || BROCHURE_COLORS.muted
  const text = opts.textColor || BROCHURE_COLORS.ink
  const size = opts.size || 15
  return lines
    .map(
      (l) => `
                <tr>
                  <td valign="top" style="padding: 0 8px 8px 0; color: ${marker}; font-family: ${BROCHURE_FONT}; font-size: ${size}px; line-height: 22px; font-weight: 700;">&ndash;</td>
                  <td valign="top" style="padding: 0 0 8px 0; color: ${text}; font-family: ${BROCHURE_FONT}; font-size: ${size}px; line-height: 22px;">${escapeHtml(l)}</td>
                </tr>`,
    )
    .join("")
}
